import React, { useState, useCallback, useEffect } from 'react';
import { CalendarEvent, EVENT_TYPE_COLORS } from '../types/calendar';
import { Filter, Palette } from 'lucide-react';
import './CalendarToolbar.css';

interface CalendarToolbarProps {
    events: CalendarEvent[];
    onFilterChange: (filteredEvents: CalendarEvent[]) => void;
    originalEvents: CalendarEvent[];
    onColorChange?: (customColors: Record<string, string>) => void;
}

export const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
    events,
    onFilterChange,
    originalEvents,
    onColorChange,
}) => {
    const [searchTerm, setSearchTerm] = useState(() => {
        const saved = sessionStorage.getItem('calendarSearchTerm');
        return saved || '';
    });
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(() => {
        const saved = sessionStorage.getItem('calendarSelectedEventTypes');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (error) {
                console.error('Error parsing saved event types:', error);
                return [];
            }
        }
        return [];
    });
    const [showFilters, setShowFilters] = useState(false);
    const [showColorCustomizer, setShowColorCustomizer] = useState(false);
    const [customColors, setCustomColors] = useState<Record<string, string>>({});

    const uniqueEventTypes = Array.from(new Set(originalEvents.map(event => event.eventType).filter(Boolean))) as string[];

    // Load custom colors from localStorage on component mount
    useEffect(() => {
        const savedColors = localStorage.getItem('calendarCustomColors');
        if (savedColors) {
            try {
                const parsedColors = JSON.parse(savedColors);
                setCustomColors(parsedColors);
                onColorChange?.(parsedColors);
            } catch (error) {
                console.error('Error parsing saved colors:', error);
            }
        }
    }, [onColorChange]);

    // Save search term to session storage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('calendarSearchTerm', searchTerm);
    }, [searchTerm]);

    // Save selected event types to session storage whenever they change
    useEffect(() => {
        sessionStorage.setItem('calendarSelectedEventTypes', JSON.stringify(selectedEventTypes));
    }, [selectedEventTypes]);

    // Save custom colors to localStorage whenever they change
    const saveCustomColors = (colors: Record<string, string>) => {
        setCustomColors(colors);
        localStorage.setItem('calendarCustomColors', JSON.stringify(colors));
        onColorChange?.(colors);
    };

    const handleColorChange = (eventType: string, color: string) => {
        const newColors = { ...customColors, [eventType]: color };
        saveCustomColors(newColors);
    };

    const resetColors = () => {
        setCustomColors({});
        localStorage.removeItem('calendarCustomColors');
        onColorChange?.({});
    };

    const getEventTypeColor = (eventType: string) => {
        return customColors[eventType] || EVENT_TYPE_COLORS[eventType as keyof typeof EVENT_TYPE_COLORS] || EVENT_TYPE_COLORS.OTHER;
    };

    const applyFilters = useCallback(() => {
        let filtered = [...originalEvents];

        // Apply search filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(event =>
                event.title.toLowerCase().includes(search) ||
                event.description.toLowerCase().includes(search) ||
                event.location?.toLowerCase().includes(search)
            );
        }

        // Apply event type filter
        if (selectedEventTypes.length > 0) {
            filtered = filtered.filter(event =>
                event.eventType && selectedEventTypes.includes(event.eventType)
            );
        }

        onFilterChange(filtered);
    }, [searchTerm, selectedEventTypes, originalEvents, onFilterChange]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const handleEventTypeChange = (eventType: string) => {
        setSelectedEventTypes(prev =>
            prev.includes(eventType)
                ? prev.filter(t => t !== eventType)
                : [...prev, eventType]
        );
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedEventTypes([]);
        sessionStorage.removeItem('calendarSearchTerm');
        sessionStorage.removeItem('calendarSelectedEventTypes');
    };

    const hasActiveFilters = searchTerm.trim() || selectedEventTypes.length > 0;

    return (
        <div className="calendar-toolbar">
            <div className="toolbar-main">
                <div className="search-box">

                    <input
                        name="search"
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <button
                    className={`filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <Filter size={16} />
                    Filters
                    {hasActiveFilters && <span className="filter-indicator" />}
                </button>

                <button
                    className={`filter-toggle ${showColorCustomizer ? 'active' : ''}`}
                    onClick={() => setShowColorCustomizer(!showColorCustomizer)}
                >
                    <Palette size={16} />
                    Colors
                    {Object.keys(customColors).length > 0 && <span className="filter-indicator" />}
                </button>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-section">
                        <h4>Event Types</h4>
                        <div className="filter-chips">
                            {uniqueEventTypes.map(eventType => (
                                <div
                                    key={eventType}
                                    className={`filter-chip ${selectedEventTypes.includes(eventType) ? 'active' : ''}`}
                                    onClick={() => handleEventTypeChange(eventType)}
                                    style={{
                                        backgroundColor: getEventTypeColor(eventType),
                                        color: 'white',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}
                                >
                                    <span className="option-label">{eventType}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="filter-actions">
                        <button
                            className="btn btn-secondary"
                            onClick={clearFilters}
                            disabled={!hasActiveFilters}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            )}

            {showColorCustomizer && (
                <div className="filter-panel">
                    <div className="filter-section">
                        <h4>Customize Event Colors</h4>
                        <div className="color-customizer">
                            {Object.keys(EVENT_TYPE_COLORS).map(eventType => (
                                <div key={eventType} className="color-picker-row">
                                    <div className="color-preview">
                                        <div 
                                            className="color-swatch"
                                            style={{ backgroundColor: getEventTypeColor(eventType) }}
                                        />
                                        <span className="event-type-label">{eventType}</span>
                                    </div>
                                    <input
                                        type="color"
                                        value={getEventTypeColor(eventType)}
                                        onChange={(e) => handleColorChange(eventType, e.target.value)}
                                        className="color-input"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="filter-actions">
                        <button
                            className="btn btn-secondary"
                            onClick={resetColors}
                            disabled={Object.keys(customColors).length === 0}
                        >
                            Reset to Defaults
                        </button>
                    </div>
                </div>
            )}

            {hasActiveFilters && (
                <div className="active-filters">
                    <span className="filter-count">
                        Showing {events.length} of {originalEvents.length} events
                    </span>
                </div>
            )}
        </div>
    );
};
